import React from 'react';
import { AlertCircle, RotateCcw, Save, Loader2 } from 'lucide-react';

export interface StickyActionBarProps {
  hasUnsavedChanges: boolean;
  isSaving: boolean;
  onReset: () => void;
  onSave: () => void;
  t: (key: string) => string;
  /** Whether to show the reset button. Default: true */
  showReset?: boolean;
  /** Custom save button label. Default: uses t('Save Changes') */
  saveLabel?: string;
  /** Custom reset button label. Default: uses t('Reset') */
  resetLabel?: string;
}

/**
 * A sticky action bar for pages with save/reset functionality.
 *
 * Usage:
 * - Place inside the page content container (e.g., within `max-w-7xl mx-auto`)
 * - Use `sticky bottom-0` positioning (not `fixed`) to avoid overlaying footer/sidebar
 * - Remove save/reset buttons from page headers when using this component
 *
 * @example
 * ```tsx
 * <StickyActionBar
 *   hasUnsavedChanges={hasUnsavedChanges}
 *   isSaving={isSaving}
 *   onReset={handleReset}
 *   onSave={handleSave}
 *   t={t}
 * />
 * ```
 */
const StickyActionBar: React.FC<StickyActionBarProps> = ({
  hasUnsavedChanges,
  isSaving,
  onReset,
  onSave,
  t,
  showReset = true,
  saveLabel,
  resetLabel,
}) => {
  return (
    <div className="sticky bottom-0 bg-card border border-border rounded-xl shadow-lg mt-6 px-6 py-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {hasUnsavedChanges && (
            <div className="flex items-center text-amber-600 text-sm">
              <AlertCircle className="w-4 h-4 mr-1" />
              {t('Unsaved changes')}
            </div>
          )}
        </div>

        <div className="flex items-center gap-3">
          {showReset && (
            <button
              onClick={onReset}
              disabled={!hasUnsavedChanges || isSaving}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-card border border-border rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RotateCcw className="w-4 h-4" />
              {resetLabel || t('Reset')}
            </button>
          )}

          <button
            onClick={onSave}
            disabled={isSaving || !hasUnsavedChanges}
            className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              hasUnsavedChanges
                ? 'text-white bg-blue-600 hover:bg-blue-700'
                : 'text-gray-500 bg-gray-100 cursor-default'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {isSaving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            {isSaving ? t('Saving...') : (saveLabel || t('Save Changes'))}
          </button>
        </div>
      </div>
    </div>
  );
};

export default StickyActionBar;
